'use client';

import base, { AxiosInstance } from 'axios';
import { createContext, useContext, useMemo, useState } from 'react';
import { AuthContext, clearStorage } from './Authentication';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { UI_ICONS } from '@/lib/ui-icons';
import { apiConfig } from '@/config/apiConfig';
import { getActiveTenantHeaders, resolveActiveRequestUserId } from '@/lib/activeTenantScope';

export const IdentityAxiosContext = createContext<AxiosInstance | null>(null);

function getIdentityInstance(auth: any, setSessionExpiredOpen: (open: boolean) => void) {
  const baseURL =
    (apiConfig.baseUrls as { identity?: string }).identity ||
    process.env.NEXT_PUBLIC_IDENTITY_SERVICE_URL ||
    'https://visitor.staging-api.boostt.ai';
  const axios = base.create({
    baseURL: baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL,
    withCredentials: true, // send cookies so identity/visitor API auth matches browser
  });

  axios.interceptors.request.use((req) => {
    const activeUserId = resolveActiveRequestUserId(auth?.userId);
    if (req.data && typeof req.data === 'object' && !(req.data instanceof FormData)) {
      if (!req.data.userID && activeUserId) req.data.userID = activeUserId;
      if (auth?.isAuthenticated && !req.data.session_token && auth?.token) {
        req.data.session_token = auth.token;
      }
    }
    req.headers['Accept'] = 'application/json';
    req.headers['X-Frontend-Page'] = typeof window !== 'undefined' ? window.location.href : '';
    Object.assign(req.headers, getActiveTenantHeaders());
    if (activeUserId) req.headers['X-User-ID'] = String(activeUserId);
    if (auth?.isAuthenticated && auth?.token) {
      req.headers['Authorization'] = `Bearer ${auth.token}`;
    }
    if (typeof document !== 'undefined' && document.cookie) {
      req.headers['cookies'] = document.cookie;
    }
    return req;
  });

  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      if (typeof window !== 'undefined' && window.navigator?.onLine && err.response) {
        if (err.response.status === 401) {
          if (auth?.isAuthenticated) {
            location.href = process.env.NEXT_PUBLIC_SELF_URL + '/v2/login';
          } else {
            setSessionExpiredOpen(true);
          }
        }
      }
      return Promise.reject(err);
    }
  );
  return axios;
}

export default function IdentityAxios({ children }: { children: React.ReactNode }) {
  const authData = useContext(AuthContext);
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const auth = authData || {};
  const client = useMemo(
    () => getIdentityInstance(auth, setSessionExpiredOpen),
    [authData] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleSessionExpiredClose = () => {
    setSessionExpiredOpen(false);
    clearStorage();
    location.href = process.env.NEXT_PUBLIC_SELF_URL + '/v2/login';
  };

  return (
    <>
      <IdentityAxiosContext.Provider value={client}>{children}</IdentityAxiosContext.Provider>
      <Dialog open={sessionExpiredOpen} onClose={handleSessionExpiredClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon={UI_ICONS.warning} sx={{ color: 'warning.main' }} />
            <Typography variant="h6" component="div">Session Expired</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">Your session has expired. Please log in again to continue.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSessionExpiredClose} variant="contained" color="primary" fullWidth>
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
